import {
  AllFavoritesResponse,
  ArchiveChannelResponse,
  ChannelMailForm,
  ChannelMailResponse,
  ChannelMemberLangResponse,
  ChannelMemberSeq,
  CreatePrivateChannelParam,
  CreateThreadChannelParam,
  FavoriteMetaResponse,
  SetFavoriteParam,
  ThreadChannelResponse,
  UnsetFavoriteResponse,
  UpdateChannelMemberRoleParam,
  UpdateChannelMemberRoleResponse,
  UpdateChannelResponse,
  UpdateFavoriteOrderParam,
  UpdateFolderTitle,
} from '@@types/Api/Channel';
import { InviteMemberResponse, RemoveMemberResponse } from '@@types/Api/Member';
import Channel, { ExportThreadChannelToTask, Management, ThreadChannel, ThreadChannelTask } from '@@types/Channel';
import ChannelFavorite from '@@types/ChannelFavorite';
import ChannelMailSetting from '@@types/ChannelMailSetting';
import {
  CHANNEL_ID,
  ContentWrap,
  FOLDER_ID,
  ID,
  MEMBER_ID,
  ORG_ID,
  RawWrap,
  StringTMap,
  THREAD_CHANNEL_ID,
} from '@@types/common';
import FolderFavorite from '@@types/FolderFavorite';
import Log from '@@types/Log';
import { Reaction } from '@@types/Log/Reaction';

import { ChannelFlag, ChannelType, SERVER_ERROR, URLS } from '~/constants';
import { DEFAULT_TIMEOUT } from '~/constants/api';
import { MINUTE } from '~/constants/date';
import Api2 from '~/stores/providers/Api2';
import { getContents, handleError, isSuccessful } from '~/utils/apiUtil';
import axios from 'axios';
import { get } from 'lodash';
import { curry } from 'ramda';

class ChannelApi {
  updateChannel = curry((channelId: CHANNEL_ID, params: Partial<Channel>): Promise<UpdateChannelResponse> => {
    return this.api.contentsRequester.put(`${URLS.API}/channel/${channelId}`, params);
  });

  inviteChannelMembers = (
    channelId: CHANNEL_ID,
    memberIds: MEMBER_ID[],
    threadChannelId?: THREAD_CHANNEL_ID
  ): Promise<InviteMemberResponse> => {
    return this.api.contentsRequester.post(
      `${URLS.API}/channel/${channelId}/member`,
      threadChannelId
        ? {
            threadChannelId,
            memberIds,
          }
        : {
            memberIds,
          }
    );
  };

  constructor(private api: Api2) {}

  fetchChannelsAndFavorites = (): Promise<AllFavoritesResponse> => {
    return this.api.contentsRequester.get(`${URLS.API}/members/me/favorites`, {
      params: {
        responseType: 'all',
        flags: `${ChannelFlag.System},${ChannelFlag.Normal},${ChannelFlag.Archived}`,
      },
    });
  };

  fetchChannel = (channelId: CHANNEL_ID): Promise<Channel> => {
    return this.api.contentsRequester.get(`${URLS.API}/channel/${channelId}`);
  };

  public fetchThreadChannel = (channelId: THREAD_CHANNEL_ID): Promise<ThreadChannelResponse> => {
    return this.api.resultRequester.get(`${URLS.API}/channel/${channelId}`);
  };

  fetchChannelMembersLang = (channelId: CHANNEL_ID): Promise<ChannelMemberLangResponse> => {
    return this.api.contentsRequester.cancelGet(
      `${URLS.API}/channel/${channelId}/members/all/preferences/lang`,
      channelId
    );
  };

  fetchChannelReadSeq = (channelId: CHANNEL_ID): Promise<ChannelMemberSeq[]> => {
    return this.api.contentsRequester.cancelGet(`${URLS.API}/channel/${channelId}/members/seq`, channelId);
  };

  unsetFavoriteInFolder = (
    folderId: FOLDER_ID,
    channelId: CHANNEL_ID,
    userId: MEMBER_ID
  ): Promise<FavoriteMetaResponse> => {
    return this.api.contentsRequester.delete(
      `${URLS.API}/members/${userId}/favorites/folders/${folderId}/channels/${channelId}`
    );
  };

  setFavorite = ({ userId, ...params }: SetFavoriteParam): Promise<ChannelFavorite | FolderFavorite> => {
    return this.api.contentsRequester.post(`${URLS.API}/members/${userId}/favorites/folders`, params);
  };

  unsetFavorite = (folderId: FOLDER_ID, isFolder: boolean, userId: MEMBER_ID): Promise<UnsetFavoriteResponse> => {
    return this.api.contentsRequester.delete(`${URLS.API}/members/${userId}/favorites/folders/${folderId}`, {
      params: { isFolder },
    });
  };

  updateFavoriteOrder = ({ userId, ...favorites }: UpdateFavoriteOrderParam): Promise<FavoriteMetaResponse> => {
    return this.api.contentsRequester.put(`${URLS.API}/members/${userId}/favorites`, favorites);
  };

  updateFolderFavoriteTitle = ({ userId, folderId, title }: UpdateFolderTitle): Promise<FolderFavorite> => {
    return this.api.contentsRequester.put(`${URLS.API}/members/${userId}/favorites/folders/${folderId}`, { title });
  };

  archiveChannel = (channelId: CHANNEL_ID): Promise<ArchiveChannelResponse> => {
    return this.api.contentsRequester.put(`${URLS.API}/channel/${channelId}/archive`);
  };

  unarchiveChannel = (channelId: CHANNEL_ID): Promise<ArchiveChannelResponse> => {
    return this.api.contentsRequester.put(`${URLS.API}/channel/${channelId}/unarchive`);
  };

  copyAndArchiveChannel = (channelId: CHANNEL_ID): Promise<Channel> => {
    return this.api.contentsRequester.post(`${URLS.API}/channel/${channelId}/copy`, {
      token: true,
    });
  };

  checkDuplicateTitle = (title: string): Promise<void> => {
    return this.api.ignoreErrorRequester
      .post<RawWrap<null>>(`${URLS.API}/channel/check-title`, {
        title,
      })
      .then((result) => {
        return isSuccessful(result) ? Promise.resolve() : Promise.reject(result);
      });
  };

  createDirectChannel = (orgId: ORG_ID, directMemberId: MEMBER_ID, selectAfterCreate = true): Promise<Channel> => {
    return this.api.contentsRequester.post(`${URLS.API}/channel/direct`, {
      type: ChannelType.Direct,
      orgId,
      capacity: 2,
      directMemberId,
      token: selectAfterCreate,
    });
  };

  getOrCreateDirectChannel = async (
    orgId: ORG_ID,
    directMemberId: MEMBER_ID,
    selectAfterCreate = true
  ): Promise<Channel> => {
    const result = await axios.post<RawWrap<ContentWrap<Channel>>>(`${URLS.API}/channel/direct`, {
      type: ChannelType.Direct,
      orgId,
      capacity: 2,
      directMemberId,
      token: selectAfterCreate,
    });

    if (isSuccessful(result) || get(result, 'data.header.resultCode') === SERVER_ERROR.CHANNEL_ALREADY_EXISTS_ERROR) {
      return getContents(result) as Channel;
    }

    return handleError(this.api.errorHandler, result);
  };

  createPrivateChannel = (
    orgId: ORG_ID,
    { title = '', description = '', capacity = 100, memberIds = [] }: CreatePrivateChannelParam
  ): Promise<Channel> => {
    const params = {
      type: ChannelType.Private,
      orgId,
      title,
      description,
      capacity,
      memberIds,
    };
    return this.api.contentsRequester.post(`${URLS.API}/channel/private`, params);
  };

  createTopicChannel = (
    orgId: ORG_ID,
    { title = '', description = '', capacity = 100, memberIds = [] }: CreatePrivateChannelParam,
    getOrCreate = false
  ): Promise<Channel> => {
    const params = {
      type: ChannelType.Private,
      orgId,
      title,
      description,
      capacity,
      memberIds,
    };
    if (getOrCreate) {
      return axios
        .post<RawWrap<ContentWrap<Channel>>>(`${URLS.API}/channel/private?caller=organizationChart`, params)
        .then((result) => {
          // prettier-ignore
          return (isSuccessful(result) || get(result, 'data.header.resultCode') === SERVER_ERROR.CHANNEL_ALREADY_SAME_JOINED_MEMBERS_EXIST_ERROR)
            ? getContents(result)
            : handleError(this.api.errorHandler, result) as Promise<Channel>;
        });
    }

    return this.api.contentsRequester.post(`${URLS.API}/channel/private`, params);
  };

  fetchThreadChannelStatus = (): Promise<{ content: { hasUnread: boolean } }> => {
    return this.api.resultRequester.get(`${URLS.API}/thread-channels/status`);
  };

  createThreadChannel = (param: CreateThreadChannelParam): Promise<ThreadChannel> => {
    return this.api.ignoreErrorRequester.post(`${URLS.API}/thread-channels`, param).then((result) => {
      if (isSuccessful(result) || get(result, 'data.header.resultCode') === SERVER_ERROR.CHANNEL_ALREADY_EXISTS_ERROR) {
        return getContents(result);
      }
      throw new Error(result.data);
    });
  };

  exportThreadChannelToTask = (channelId: THREAD_CHANNEL_ID, withAI = false): Promise<ExportThreadChannelToTask> => {
    return this.api.resultRequester.post(
      `${URLS.API}/thread/${channelId}/draft${withAI ? '?withAi=true' : ''}`,
      {},
      { timeout: withAI ? MINUTE : DEFAULT_TIMEOUT }
    );
  };

  fetchThreadChannelTasks = (channelId: THREAD_CHANNEL_ID): Promise<ThreadChannelTask[]> => {
    return this.api.resultRequester.get(`${URLS.API}/thread/${channelId}/tasks`);
  };

  deleteThreadChannelTask = (channelId: THREAD_CHANNEL_ID, taskId: ID) => {
    return this.api.resultRequester.delete(`${URLS.API}/thread/${channelId}/tasks/${taskId}`);
  };

  leaveChannel = (channelId: CHANNEL_ID): Promise<void> => {
    return this.api.contentsRequester.put(`${URLS.API}/channel/${channelId}/member`, {
      channelId,
    });
  };

  removeMembers = (channelId: CHANNEL_ID, memberIds: MEMBER_ID[]): Promise<RemoveMemberResponse> => {
    return this.api.contentsRequester.put(`${URLS.API}/channel/${channelId}/members`, {
      memberIds,
    });
  };

  updateChannelDisplayed = (channelId: CHANNEL_ID, displayed: boolean) => {
    return this.api.contentsRequester.put(`${URLS.API}/channel/${channelId}/display`, {
      displayed,
    });
  };

  fetchThreadChannelList = (params: {
    page: number;
    size: number;
    sentAt?: number;
    filter: 'all' | 'unread';
  }): Promise<{
    contents: ThreadChannel[];
    references: {
      channelMap: StringTMap<Channel>;
      channelLogMap: StringTMap<Log>;
      originalLogMap: StringTMap<Log>;
      reactionMap: StringTMap<Reaction>;
    };
  }> => {
    return this.api.resultRequester.get(`${URLS.API}/thread-channels`, { params });
  };

  // Auth
  updateChannelManagement = (channelId: CHANNEL_ID, value: Management): Promise<Channel> => {
    return this.api.contentsRequester.put(`${URLS.API}/channels/${channelId}/management`, value);
  };

  updateChannelMemberRole = ({
    channelId,
    memberId,
    ...params
  }: UpdateChannelMemberRoleParam): Promise<UpdateChannelMemberRoleResponse> => {
    return this.api.contentsRequester.post(`${URLS.API}/channel/${channelId}/members/${memberId}/set-role`, params);
  };

  // Mail
  fetchChannelMailSetting = (channelId): Promise<ChannelMailResponse> => {
    return this.api.contentsRequester.cancelGet(`${URLS.API}/channels/${channelId}/mail`, channelId);
  };

  registerChannelMail = ({ channelId, ...params }: ChannelMailForm): Promise<ChannelMailSetting> => {
    return this.api.contentsRequester.post(`${URLS.API}/channels/${channelId}/mail`, params);
  };

  unregisterChannelMail = (channelId: CHANNEL_ID): Promise<void> => {
    return this.api.contentsRequester.delete(`${URLS.API}/channels/${channelId}/mail`);
  };

  updateChannelMail = ({ channelId, ...params }: ChannelMailForm): Promise<ChannelMailSetting> => {
    return this.api.contentsRequester.put(`${URLS.API}/channels/${channelId}/mail`, params);
  };

  // Translation
  updateChannelTranslation = (channelId: CHANNEL_ID, value: boolean): Promise<Channel> => {
    const queryParams = `?translate=${value ? 1 : 0}`;
    return this.api.contentsRequester.put(`${URLS.API}/channels/${channelId}/translate${queryParams}`);
  };
}

export default ChannelApi;
